import { mergeTranslations, type I18nProvider } from "ra-core";
import polyglotI18nProvider from "ra-i18n-polyglot";
import englishMessages from "ra-language-english";
import frenchMessages from "ra-language-french";
import { raSupabaseEnglishMessages } from "ra-supabase-language-english";
import { raSupabaseFrenchMessages } from "ra-supabase-language-french";
import { englishCrmMessages } from "./englishCrmMessages";
import { frenchCrmMessages } from "./frenchCrmMessages";
// GAVION: Gavion catalogs and locale resolution
import { gavionEnMessages } from "../../../gavion/i18n/en";
import { gavionFrMessages } from "../../../gavion/i18n/fr";
import {
  getStoredLocale,
  resolveInitialLocale,
  storeLocale,
} from "../../../gavion/i18n/locale";

const raSupabaseEnglishMessagesOverride = {
  "ra-supabase": {
    auth: {
      password_reset: "Check your emails for a Reset Password message.",
    },
  },
};

const raSupabaseFrenchMessagesOverride = {
  "ra-supabase": {
    auth: {
      password_reset:
        "Consultez vos emails pour trouver le message de reinitialisation du mot de passe.",
    },
  },
};

const englishCatalog = mergeTranslations(
  englishMessages,
  raSupabaseEnglishMessages,
  raSupabaseEnglishMessagesOverride,
  englishCrmMessages,
  gavionEnMessages, // GAVION: Gavion catalogs
);

const frenchCatalog = mergeTranslations(
  englishCatalog,
  frenchMessages,
  raSupabaseFrenchMessages,
  raSupabaseFrenchMessagesOverride,
  frenchCrmMessages,
  gavionFrMessages, // GAVION: Gavion catalogs
);

export const getInitialLocale = (): "en" | "fr" => {
  if (typeof navigator === "undefined") {
    return "en";
  }

  const browserLocale = navigator.languages?.[0] ?? navigator.language;
  if (browserLocale?.toLowerCase().startsWith("fr")) {
    return "fr";
  }

  return "en";
};

const polyglotProvider = polyglotI18nProvider(
  (locale) => {
    if (locale === "fr") {
      return frenchCatalog;
    }
    return englishCatalog;
  },
  resolveInitialLocale(getStoredLocale()), // GAVION: French default + persisted choice
  [
    { locale: "en", name: "English" },
    { locale: "fr", name: "Français" },
  ],
  { allowMissing: true },
);

// GAVION: persist every locale change centrally (profile page, mobile settings, toggle)
export const i18nProvider: I18nProvider = {
  ...polyglotProvider,
  changeLocale: (locale, options) => {
    if (locale === "fr" || locale === "en") storeLocale(locale);
    return polyglotProvider.changeLocale(locale, options);
  },
};

export const testI18nProvider = polyglotI18nProvider(
  () => englishCatalog,
  "en",
  [{ locale: "en", name: "English" }],
  { allowMissing: true },
);
